import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {FadeInAnimation} from 'UI/animations/shared';

import {useStorePartner} from './useStorePartner';

import type {ImageUpload} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The class name to apply to the component for custom styling.
     */
    className?: string;
    /**
     * An object representing the store's logo, with alternative text, dimensions, and source URL.
     */
    logo?: ImageUpload;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'StorePartner'
     */
    testId?: string;
    /**
     * The URL to the retailer's online store, which will be linked via a button in the component.
     * Clicking the button will open the retailer's store in a new tab.
     */
    url: string;
}

/**
 * The `StorePartner` component displays a partner's logo with a link that opens in a new tab.
 * It supports animations, dynamic classes, and accessible logo properties.
 * If a logo URL, width, and height are provided, the logo is rendered inside an SVG element.
 *
 * @param props             The component props.
 * @param props.className   An optional class name for custom styling.
 * @param props.logo        An object containing logo details such as `alt` text, `height`, `width`, and `url`.
 * @param props.logo.alt    The alternative text for the logo image.
 * @param props.logo.height The height of the logo image.
 * @param props.logo.url    The URL of the logo image.
 * @param props.logo.width  The width of the logo image.
 * @param props.testId      An optional unique identifier for testing purposes, defaulting to `'StorePartner'`.
 * @param props.url         The URL that the partner logo links to.
 * @returns A JSX element representing the `StorePartner` component.
 *
 * @example
 * ```tsx
 * <StorePartner
 *   className="partner-class"
 *   logo={{
 *     alt: "Partner Logo",
 *     height: 100,
 *     width: 200,
 *     url: "/path/to/logo.svg"
 *   }}
 *   url="https://partner-website.com"
 * />
 * ```
 */
const StorePartner = observer(({
    className,
    logo,
    testId = 'StorePartner',
    url
}: ComponentProps) => {
    const {alt, height, logoUrl, width} = useStorePartner(logo);

    return (
        <Wrapper
            className={className}
            data-cy={testId}
            href={url}
            target="_blank"
            variants={FadeInAnimation}
        >
            {logoUrl && width && height && (
                <svg aria-label={alt} viewBox={`0 0 ${width} ${height}`}>
                    <use height={height} href={`${logoUrl}#Icon`} width={width} />
                </svg>
            )}
        </Wrapper>
    );
});

StorePartner.displayName = 'StorePartner';

export {StorePartner};

const Wrapper = styled(motion.a)`
    align-items: center;
    background-color: ${({theme}) => theme.colors.primary};
    color: ${({theme}) => theme.colors.bgSecondary};
    display: flex;
    flex-direction: column;
    padding-block: ${spacing(6)};
    padding-inline: ${spacing(6)};
    transition: background-color 0.2s ease-in-out;
    will-change: background-color;

    &:hover {
        background-color: ${({theme}) => theme.colors.tertiary};
    }

    ${media('sm')} {
        padding-block: ${spacing(8)};
    }

    svg {
        height: 6rem;
        max-width: 100%;
        pointer-events: none;
        width: 15rem;
    }
`;